import React, { useCallback, useMemo, useState } from "react";
import {
    Stack,
    Title,
    Text,
    Table,
    TextInput,
    Button,
    Pagination,
    Group,
    ScrollArea,
    TableThead,
    TableTbody,
    TableTr,
    TableTh,
    TableTd,
    Select,
    rem,
    Modal,
    Alert,
    Loader,
    Center,
    Paper,
    Box,
    Badge,
    LoadingOverlay,
    Flex,
    UnstyledButton,
    ActionIcon
} from "@mantine/core";
import { MdArrowDownward, MdArrowUpward, MdSearch, MdClose, MdFileDownload, MdGroup, MdRefresh, MdOpenInNew } from "react-icons/md";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../authConfig";
import { getUsersListApi, exportUsersApi, UserListItem } from "../../api";
import { LoadErrorAlert } from "../common/LoadErrorAlert";
import { useAbortableFetch } from "../../hooks/useAbortableFetch";

type UserItem = UserListItem;

const USER_COLUMNS: Array<{
    key: keyof UserListItem;
    label: string;
    width: number;
    flexible: boolean;
    sortable?: boolean;
}> = [
    { key: "name", label: "User", width: 280, flexible: true, sortable: true },
    { key: "entra_oid", label: "Entra / Okta ID", width: 320, flexible: false, sortable: false }
];

export interface UserTableProps {
    title?: string;
    subtitle?: string;
    compact?: boolean;
    showExport?: boolean;
    showSearch?: boolean;
    showPagination?: boolean;
    showRefresh?: boolean;
    maxHeight?: string;
    onUserClick?: (userId: string) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
    title = "All Users",
    subtitle = "Search, sort, and export the organisation's accounts.",
    compact = false,
    showExport = true,
    showSearch = true,
    showPagination = true,
    showRefresh = true,
    maxHeight = "60vh",
    onUserClick
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
    const [search, setSearch] = useState(() => searchParams.get("search") || "");

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportStatus, setExportStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [exportError, setExportError] = useState<string>("");

    const [sortBy, setSortBy] = useState<keyof UserItem>(() => {
        const urlSortBy = searchParams.get("sortBy");
        // Default to "name" if invalid sort field
        return (urlSortBy as keyof UserItem) || "name";
    });
    const [sortAsc, setSortAsc] = useState(() => {
        const sortAscParam = searchParams.get("sortAsc");
        return sortAscParam !== null ? sortAscParam === "true" : true;
    });
    const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);
    const [pageSize, setPageSize] = useState(() => Number(searchParams.get("pageSize")) || 15);

    const navigate = useNavigate();
    const client = useLogin ? useMsal().instance : undefined;

    const updateSearchParams = (updates: Record<string, string | number | boolean>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });
        setSearchParams(newParams);
    };

    const fetchUsers = useCallback(
        async (signal: AbortSignal) => {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available for fetching users list");

            return getUsersListApi(idToken, page, pageSize, sortBy, sortAsc ? "asc" : "desc", search, undefined, undefined, signal);
        },
        [client, page, pageSize, sortBy, sortAsc, search]
    );

    const { data, loading, error, reload: fetchData } = useAbortableFetch(fetchUsers, "Failed to fetch users list");
    const users = useMemo<UserItem[]>(() => data?.users || [], [data]);
    const totalPages = data?.total_pages || 1;
    const totalItems = data?.total_items || 0;

    const handleSort = (column: keyof UserItem) => {
        if (loading) return;
        const newSortAsc = sortBy === column ? !sortAsc : true;
        setSortBy(column);
        setSortAsc(newSortAsc);
        updateSearchParams({ sortBy: column, sortAsc: newSortAsc });
    };

    const openUserDetails = (userId: string) => {
        if (!loading) {
            if (onUserClick) {
                onUserClick(userId);
            } else {
                navigate(`/administration/users/${userId}?${searchParams.toString()}`);
            }
        }
    };

    const openUserDetailsInNewTab = (userId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        if (!loading) {
            window.open(`/#/administration/users/${userId}?${searchParams.toString()}`, "_blank");
        }
    };

    const applySearch = () => {
        const newSearch = searchInput.trim().toLowerCase();
        setSearch(newSearch);
        setPage(1);
        updateSearchParams({ search: newSearch, page: 1 });
    };

    const clearSearch = () => {
        setSearchInput("");
        setSearch("");
        setPage(1);
        updateSearchParams({ search: "", page: 1 });
    };

    const handleRefresh = () => {
        fetchData();
    };

    const openExportModal = () => {
        setExportModalOpen(true);
        setExportStatus("idle");
        setExportError("");
    };

    const closeExportModal = () => {
        if (exportStatus === "loading") return;
        setExportModalOpen(false);
        setExportStatus("idle");
        setExportError("");
    };

    const cancelExport = () => {
        if (exportStatus === "loading") {
            setExportStatus("idle");
        }
    };

    const handleModalExport = async () => {
        try {
            setExportStatus("loading");
            setExportError("");

            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No authentication token available for export");

            const blob = await exportUsersApi(idToken);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "users_export.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);

            setExportStatus("success");
        } catch (err) {
            console.error("Failed to export users", err);
            setExportError(err instanceof Error ? err.message : "Export failed");
            setExportStatus("error");
        }
    };

    const exportDisabled = loading || exportStatus === "loading";

    const renderSortIcon = (column: keyof UserItem) => {
        const isActive = sortBy === column;
        const style = { opacity: isActive ? 1 : 0.25, transition: "opacity 120ms ease" } as const;
        if (!isActive || sortAsc) {
            return <MdArrowUpward size={14} style={style} />;
        }
        return <MdArrowDownward size={14} style={style} />;
    };

    const renderSortableHeader = (columnKey: keyof UserItem, label: string) => (
        <UnstyledButton
            onClick={() => handleSort(columnKey)}
            disabled={loading}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: sortBy === columnKey ? "var(--mantine-color-blue-6)" : "var(--mantine-color-dark-6)",
                fontWeight: 600
            }}
        >
            <Text fw={600} size="sm">
                {label}
            </Text>
            {renderSortIcon(columnKey)}
        </UnstyledButton>
    );

    const renderCell = (columnKey: keyof UserItem, user: UserItem) => {
        switch (columnKey) {
            case "name":
                return (
                    <Group gap="sm" justify="space-between" wrap="nowrap">
                        <Stack gap={2} maw={340} style={{ flex: 1 }}>
                            <Text fw={600}>{user.name || "Unnamed user"}</Text>
                            <Text size="xs" c="dimmed">
                                {user.username}
                            </Text>
                        </Stack>
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={e => openUserDetailsInNewTab(user.entra_oid, e)}
                            disabled={loading}
                            title="Open in new tab"
                            style={{ alignSelf: "flex-start" }}
                        >
                            <MdOpenInNew size={16} />
                        </ActionIcon>
                    </Group>
                );
            case "entra_oid":
                return (
                    <Text ff="monospace" size="sm">
                        {user.entra_oid}
                    </Text>
                );
            default:
                return <Text>{String(user[columnKey as keyof UserItem] ?? "")}</Text>;
        }
    };

    return (
        <Paper
            withBorder
            radius="lg"
            shadow="sm"
            p="lg"
            style={{
                display: "flex",
                flexDirection: "column",
                gap: rem(24),
                flex: 1,
                minHeight: compact ? "auto" : "28rem",
                overflow: "hidden"
            }}
        >
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                <Stack gap={8} maw={480}>
                    <Title order={compact ? 4 : 3} size={compact ? "1.2rem" : "1.4rem"}>
                        {title}
                    </Title>
                    <Group gap="xs">
                        <Badge color="blue" variant="light" size="sm" radius="sm">
                            {totalItems.toLocaleString()} total
                        </Badge>
                        {search && (
                            <Badge color="grape" variant="light" size="sm" radius="sm">
                                Filter active
                            </Badge>
                        )}
                    </Group>
                    {subtitle && (
                        <Text size="sm" c="dimmed">
                            {subtitle}
                        </Text>
                    )}
                </Stack>
                <Group gap="sm">
                    {showRefresh && (
                        <Button variant="subtle" size="sm" leftSection={<MdRefresh size={16} />} onClick={handleRefresh} disabled={loading}>
                            Refresh
                        </Button>
                    )}
                    {showExport && (
                        <Button variant="subtle" size="sm" leftSection={<MdFileDownload size={16} />} onClick={openExportModal} disabled={exportDisabled}>
                            Export
                        </Button>
                    )}
                </Group>
            </Group>

            {showSearch && (
                <Flex gap="sm" wrap="wrap" align="center">
                    <TextInput
                        placeholder="Search users by name or username"
                        leftSection={<MdSearch size={16} />}
                        rightSection={
                            searchInput && (
                                <ActionIcon onClick={clearSearch} variant="subtle" size="sm">
                                    <MdClose size={14} />
                                </ActionIcon>
                            )
                        }
                        value={searchInput}
                        disabled={exportDisabled}
                        onChange={e => setSearchInput(e.currentTarget.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") applySearch();
                        }}
                        style={{ flex: "1 1 260px", minWidth: "220px" }}
                    />
                    <Button onClick={applySearch} disabled={exportDisabled} variant="filled" leftSection={<MdSearch size={16} />}>
                        Search
                    </Button>
                </Flex>
            )}

            <Box pos="relative" style={{ flex: 1, minHeight: compact ? 200 : 280 }}>
                <LoadingOverlay visible={loading} />

                {error ? (
                    <LoadErrorAlert title="Couldn't load users" error={error} onRetry={fetchData} />
                ) : users.length === 0 && !loading ? (
                    <Center h="100%" mih={220}>
                        <Stack gap="xs" align="center">
                            <MdGroup size={36} color="var(--mantine-color-gray-5)" />
                            <Text fw={600}>No users found</Text>
                            <Text size="sm" c="dimmed" ta="center">
                                Try adjusting your search filters.
                            </Text>
                        </Stack>
                    </Center>
                ) : (
                    <ScrollArea.Autosize mah={maxHeight} type="auto">
                        <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ fontSize: "0.9rem" }}>
                            <TableThead style={{ backgroundColor: "var(--mantine-color-gray-0)" }}>
                                <TableTr>
                                    {USER_COLUMNS.map(column => (
                                        <TableTh key={column.label} style={{ minWidth: column.width }}>
                                            {column.sortable === false ? (
                                                <Text fw={600} size="sm">
                                                    {column.label}
                                                </Text>
                                            ) : (
                                                renderSortableHeader(column.key as keyof UserItem, column.label)
                                            )}
                                        </TableTh>
                                    ))}
                                </TableTr>
                            </TableThead>
                            <TableTbody>
                                {users.map(user => (
                                    <TableTr
                                        key={user.entra_oid}
                                        onClick={() => openUserDetails(user.entra_oid)}
                                        style={{ cursor: loading ? "default" : "pointer" }}
                                    >
                                        {USER_COLUMNS.map(column => (
                                            <TableTd key={column.label} style={{ minWidth: column.width }}>
                                                {renderCell(column.key, user)}
                                            </TableTd>
                                        ))}
                                    </TableTr>
                                ))}
                            </TableTbody>
                        </Table>
                    </ScrollArea.Autosize>
                )}
            </Box>

            {showPagination && (
                <Group justify="space-between" mt="sm">
                    <Select
                        value={String(pageSize)}
                        onChange={val => {
                            if (val) {
                                const newPageSize = Number(val);
                                setPageSize(newPageSize);
                                setPage(1);
                                updateSearchParams({ pageSize: newPageSize, page: 1 });
                            }
                        }}
                        data={[
                            { value: "10", label: "10 / page" },
                            { value: "15", label: "15 / page" },
                            { value: "25", label: "25 / page" },
                            { value: "50", label: "50 / page" },
                            { value: "100", label: "100 / page" }
                        ]}
                        w={150}
                        disabled={loading}
                    />
                    <Pagination
                        total={totalPages}
                        value={page}
                        onChange={newPage => {
                            setPage(newPage);
                            updateSearchParams({ page: newPage });
                        }}
                        disabled={loading}
                    />
                </Group>
            )}

            {showExport && (
                <Modal
                    opened={exportModalOpen}
                    onClose={closeExportModal}
                    title="Export Users"
                    size="md"
                    closeOnClickOutside={exportStatus !== "loading"}
                    closeOnEscape={exportStatus !== "loading"}
                >
                    <Stack gap="md">
                        {exportStatus === "idle" && (
                            <>
                                <Text size="sm" c="dimmed">
                                    Export the complete user directory including activity totals.
                                </Text>
                                <Group justify="flex-end" mt="md">
                                    <Button variant="subtle" onClick={closeExportModal}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleModalExport} leftSection={<MdFileDownload size={16} />}>
                                        Export
                                    </Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "loading" && (
                            <>
                                <Center py="xl">
                                    <Stack gap="md" align="center">
                                        <Loader size="lg" />
                                        <Text size="sm">Exporting users data...</Text>
                                        <Text size="xs" c="dimmed">
                                            This may take a moment
                                        </Text>
                                    </Stack>
                                </Center>
                                <Group justify="center">
                                    <Button variant="subtle" onClick={cancelExport}>
                                        Cancel
                                    </Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "success" && (
                            <>
                                <Center py="xl">
                                    <Stack gap="sm" align="center">
                                        <MdFileDownload size={32} color="var(--mantine-color-green-6)" />
                                        <Text fw={600}>Export complete!</Text>
                                        <Text size="sm" c="dimmed">
                                            The file has been downloaded to your device.
                                        </Text>
                                    </Stack>
                                </Center>
                                <Group justify="center">
                                    <Button onClick={closeExportModal}>Close</Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "error" && (
                            <>
                                <Alert color="red" title="Export failed">
                                    {exportError}
                                </Alert>
                                <Group justify="flex-end" mt="md">
                                    <Button variant="subtle" onClick={closeExportModal}>
                                        Close
                                    </Button>
                                    <Button onClick={handleModalExport} leftSection={<MdFileDownload size={16} />}>
                                        Try again
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Stack>
                </Modal>
            )}
        </Paper>
    );
};
